/**
 * FuarTalepTurleriTab.jsx — "Bu fuarda hangi talepler yapılabilir"
 * Fuar Kuralları ekranının (FuarKurallariPage.jsx) "Talep Türleri" sekmesi.
 *
 * Kaynak: `stand_job_type_to_fuar`. CANLI, her fuar için (mock yok):
 *   GET  /fuar/allJobTypes          — katalog (talep_group ile)
 *   GET  /fuar/{id}/job-types       — bu fuarın izin listesi
 *   POST /fuar/{id}/job-types       — { standJobTypeIds } sil + yeniden yaz
 * Rehber: FE-StandTalepRequestController-guide.md "Per-fair catalogue filter",
 * FE-changed-payloads-guide.md §4.
 *
 * ⚠️ OPT-IN İZİN LİSTESİ — ekranın bütün tasarımı bu kurala dayanıyor:
 *    · fuarın HİÇ satırı yoksa → TÜM katalog açık (varsayılan, ~300 fuar böyle)
 *    · TEK bir satır bile varsa → YALNIZCA listedekiler; diğer gruplar da dahil.
 *      Sadece bir İş İzni türü seçmek Hizmet sekmesini BOŞALTIR.
 *    · `[]` kaydetmek "hiçbir talep" DEĞİL, "kısıtlama yok" demek. "Hiçbir talep
 *      yapılamasın" backend'de ifade edilemiyor — bu yüzden kısıtlı modda sıfır seçim
 *      kaydedilemiyor.
 * ⚠️ Teknik talepler (elektrik/su/hava/internet) bu listeye TABİ DEĞİL — Java sabiti.
 * ⚠️ Bir türü kapatmak mevcut talepleri silmez; o türdeki eski talepler grup
 *    listelerinden düşer ama /ozet özetinde görünmeye devam eder.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Row, Col, Button, ButtonGroup, Badge, Alert, Spinner, Input, Label,
  FormGroup, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { fuarService, apiErrorMessage } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const TALEP_GROUPS = [
  { id: 1, get label() { return i18n.t("yonetim:fuarTalepTurleriTab.hizmet"); }, icon: "bx-support" },
  { id: 2, get label() { return i18n.t("yonetim:fuarTalepTurleriTab.isIzinleri"); }, icon: "bx-hard-hat" },
  { id: 3, get label() { return i18n.t("yonetim:fuarTalepTurleriTab.ekipman"); }, icon: "bx-package" },
  // talep_group 0: yeni talep sekmelerinde görünmez, eski stand_talep akışında kullanılır.
  { id: 0, get label() { return i18n.t("yonetim:fuarTalepTurleriTab.digerEskiAkis"); }, icon: "bx-archive" },
];
const KNOWN_GROUP_IDS = TALEP_GROUPS.map((g) => g.id);
const groupOf = (row) => (KNOWN_GROUP_IDS.includes(Number(row.talep_group)) ? Number(row.talep_group) : 0);

const MODE = { ALL: "all", RESTRICTED: "restricted" };

const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

export default function FuarTalepTurleriTab({ fuarId }) {
  const { t: tx } = useTranslation("yonetim");
  const [catalogue, setCatalogue] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [mode, setMode] = useState(MODE.ALL);
  const [draftIds, setDraftIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4500);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!fuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [all, allowed] = await Promise.all([
          fuarService.getAllJobTypes(),
          fuarService.getFuarJobTypes(fuarId),
        ]);
        if (ignore) return;
        const ids = new Set(allowed.map((r) => Number(r.standJobTypeId)));
        setCatalogue(all);
        setSavedIds(ids);
        setDraftIds(new Set(ids));
        setMode(ids.size === 0 ? MODE.ALL : MODE.RESTRICTED);
      } catch (e) {
        if (!ignore) setError(apiErrorMessage(e, i18n.t("yonetim:fuarTalepTurleriTab.talepTurleriYuklenemedi")));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, reloadToken]);

  const catalogueIds = useMemo(() => catalogue.map((r) => Number(r.stand_job_type_id)), [catalogue]);
  const byGroup = useMemo(() => {
    const m = new Map(TALEP_GROUPS.map((g) => [g.id, []]));
    catalogue.forEach((r) => m.get(groupOf(r)).push(r));
    return m;
  }, [catalogue]);

  const savedMode = savedIds.size === 0 ? MODE.ALL : MODE.RESTRICTED;
  // Kaydedilecek küme: tüm-açık modda boş liste (backend'in "kısıtlama yok"u).
  const targetIds = mode === MODE.ALL ? new Set() : draftIds;
  const dirty = mode !== savedMode || !sameSet(targetIds, savedIds);
  const restrictedEmpty = mode === MODE.RESTRICTED && draftIds.size === 0;
  // Katalogda artık olmayan (silinmiş türe işaret eden) kayıtlı satırlar — kaybetme.
  const orphanIds = [...draftIds].filter((id) => !catalogueIds.includes(id));

  const isOpen = (id) => (mode === MODE.ALL ? true : draftIds.has(id));

  function switchMode(next) {
    if (next === mode) return;
    setMode(next);
    // Kısıtlamaya geçerken hiçbir şey seçili değilse HEPSİNİ seçili başlat: kullanıcı
    // istemediğini kapatır. Boş başlatmak, tek bir tık sonrası kaydedince tüm diğer
    // grupları sessizce boşaltırdı.
    if (next === MODE.RESTRICTED && draftIds.size === 0) setDraftIds(new Set(catalogueIds));
  }

  function toggle(id) {
    setDraftIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function setGroup(groupId, on) {
    const ids = (byGroup.get(groupId) || []).map((r) => Number(r.stand_job_type_id));
    setDraftIds((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
      return n;
    });
  }

  function resetDraft() {
    setDraftIds(new Set(savedIds));
    setMode(savedMode);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await fuarService.setFuarJobTypes(fuarId, [...targetIds]);
      setConfirmOpen(false);
      setSuccessMsg(
        mode === MODE.ALL
          ? tx("fuarTalepTurleriTab.kisitlamaKaldirildiBuFuardaTum")
          : tx("fuarTalepTurleriTab.kaydedildiBuFuardaTalepTuru", { size: targetIds.size })
      );
      setReloadToken((n) => n + 1);
    } catch (e) {
      setError(apiErrorMessage(e, tx("fuarTalepTurleriTab.talepTurleriKaydedilemedi")));
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  }

  // Onay modalı için fark: kaydedilmiş ETKİN küme ile hedef ETKİN küme.
  const effective = (ids) => (ids.size === 0 ? new Set(catalogueIds) : ids);
  const beforeOpen = effective(savedIds);
  const afterOpen = effective(targetIds);
  const nameOf = (id) =>
    catalogue.find((r) => Number(r.stand_job_type_id) === id)?.stand_job_type_name ?? `#${id}`;
  const closing = catalogueIds.filter((id) => beforeOpen.has(id) && !afterOpen.has(id));
  const opening = catalogueIds.filter((id) => !beforeOpen.has(id) && afterOpen.has(id));
  const emptiedGroups = mode === MODE.RESTRICTED
    ? TALEP_GROUPS.filter((g) => g.id !== 0 && (byGroup.get(g.id) || []).length > 0
        && (byGroup.get(g.id) || []).every((r) => !draftIds.has(Number(r.stand_job_type_id))))
    : [];

  if (loading) {
    return <div className="text-center py-5"><Spinner color="primary" /></div>;
  }

  return (
    <>
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert color="success">{successMsg}</Alert>}

      {/* 2026-09-15 sadeleştirme: mod kartları ve açıklama paragrafları kaldırıldı;
          opt-in izin listesinin kuralları dosya başında. */}
      <Card>
        <CardHeader className="d-flex align-items-center gap-3 flex-wrap">
          <h6 className="card-title mb-0 flex-grow-1">
            {tx("fuarTalepTurleriTab.talepTurleri")}
            <span className="text-muted fw-normal small ms-2">
              {savedMode === MODE.ALL ? tx("fuarTalepTurleriTab.kayitliTumuAcik") : tx("fuarTalepTurleriTab.kayitliTurAcik", { size: savedIds.size })}
            </span>
          </h6>
          <ButtonGroup size="sm">
            <Button color="primary" outline={mode !== MODE.ALL} onClick={() => switchMode(MODE.ALL)}>
              {tx("fuarTalepTurleriTab.tumuAcik")}
            </Button>
            <Button color="primary" outline={mode !== MODE.RESTRICTED} onClick={() => switchMode(MODE.RESTRICTED)}>
              {tx("fuarTalepTurleriTab.yalnizcaSecilenler")}
            </Button>
          </ButtonGroup>
        </CardHeader>
        <CardBody className="py-2">
          <span className="text-muted small">
            {mode === MODE.ALL
              ? tx("fuarTalepTurleriTab.katalogdakiHerTalepTuruBu")
              : tx("fuarTalepTurleriTab.isaretlenmeyenTurlerBuFuarinTalep")}
            {tx("fuarTalepTurleriTab.teknikTaleplerElektrikSuHava")}
          </span>
        </CardBody>
      </Card>

      {restrictedEmpty && (
        <Alert color="danger" className="small py-2">
          {tx("fuarTalepTurleriTab.enAzBirTurSecin")}
        </Alert>
      )}
      {emptiedGroups.length > 0 && !restrictedEmpty && (
        <Alert color="warning" className="small py-2">
          {emptiedGroups.map((g) => g.label).join(", ")}{tx("fuarTalepTurleriTab.hicbirTurAcikDegilSekme")}
        </Alert>
      )}

      <Row>
        {TALEP_GROUPS.map((g) => {
          const rows = byGroup.get(g.id) || [];
          if (rows.length === 0) return null;
          const openCount = rows.filter((r) => isOpen(Number(r.stand_job_type_id))).length;
          return (
            <Col lg={6} key={g.id} className="mb-3">
              <Card className="h-100 mb-0">
                <CardHeader className="d-flex align-items-center gap-2 py-2">
                  <i className={`bx ${g.icon} text-muted fs-5`} />
                  <span className="fw-semibold flex-grow-1">{g.label}</span>
                  <span className={`small ${openCount === 0 ? "text-danger" : "text-muted"}`}>
                    {openCount}/{rows.length}
                  </span>
                  {mode === MODE.RESTRICTED && (
                    <>
                      <Button color="link" size="sm" className="p-0 ms-2" onClick={() => setGroup(g.id, true)}>{tx("common:tumu")}</Button>
                      <Button color="link" size="sm" className="p-0 text-muted" onClick={() => setGroup(g.id, false)}>{tx("fuarTalepTurleriTab.hicbiri")}</Button>
                    </>
                  )}
                </CardHeader>
                <CardBody className="py-2">
                  {rows.map((r) => {
                    const id = Number(r.stand_job_type_id);
                    return (
                      <FormGroup check key={id} className="py-1 mb-0">
                        <Input
                          type="checkbox" id={`jt-${id}`}
                          checked={isOpen(id)} disabled={mode === MODE.ALL}
                          onChange={() => toggle(id)}
                        />
                        <Label
                          check for={`jt-${id}`}
                          className={isOpen(id) ? "" : "text-muted"}
                          title={r.stand_job_type_string ? tx("fuarTalepTurleriTab.istenenBilgi", { stand_job_type_string: r.stand_job_type_string }) : undefined}
                        >
                          {r.stand_job_type_name}
                        </Label>
                      </FormGroup>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Kayıtlı listede katalogda artık olmayan id'ler kaydederken korunuyor (orphanIds). */}
      {orphanIds.length > 0 && (
        <div className="text-muted small mb-3">{tx("fuarTalepTurleriTab.katalogdaOlmayanKayitliTurId")}{" "}{orphanIds.join(", ")}</div>
      )}

      {dirty && (
        <Card className="position-sticky bottom-0 mb-0 shadow-lg" style={{ zIndex: 2 }}>
          <CardBody className="py-2 d-flex align-items-center justify-content-between gap-2">
            <span className="small text-muted">{tx("fuarTalepTurleriTab.kaydedilmemisDegisiklik")}</span>
            <div className="d-flex gap-2">
              <Button color="light" size="sm" onClick={resetDraft} disabled={saving}>{tx("fuarTalepTurleriTab.vazgec")}</Button>
              <Button color="primary" size="sm" onClick={() => setConfirmOpen(true)} disabled={saving || restrictedEmpty}>
                <i className="bx bx-save me-1" />{tx("common:kaydet")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={confirmOpen} toggle={() => !saving && setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => !saving && setConfirmOpen(false)}>{tx("fuarTalepTurleriTab.talepTurleriniKaydet")}</ModalHeader>
        <ModalBody>
          <p className="mb-3">
            {mode === MODE.ALL
              ? tx("fuarTalepTurleriTab.buFuardaTumTalepTurleri")
              : tx("fuarTalepTurleriTab.buFuardaTalepTuruAcik", { size: targetIds.size })}
          </p>
          {closing.length > 0 && (
            <div className="mb-2">
              <div className="small fw-semibold text-danger mb-1">{tx("fuarTalepTurleriTab.kapanacak")}</div>
              <div className="d-flex flex-wrap gap-1">
                {closing.map((id) => <Badge key={id} color="danger-subtle" className="text-danger">{nameOf(id)}</Badge>)}
              </div>
            </div>
          )}
          {opening.length > 0 && (
            <div className="mb-2">
              <div className="small fw-semibold text-success mb-1">{tx("fuarTalepTurleriTab.acilacak")}</div>
              <div className="d-flex flex-wrap gap-1">
                {opening.map((id) => <Badge key={id} color="success-subtle" className="text-success">{nameOf(id)}</Badge>)}
              </div>
            </div>
          )}
          <div className="text-muted small mt-2">{tx("fuarTalepTurleriTab.mevcutTaleplerSilinmez")}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmOpen(false)} disabled={saving}>{tx("fuarTalepTurleriTab.vazgec")}</Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}{tx("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
